import type { FC } from 'react';
import { useState } from 'react';
import { AlertTriangle, Check, MapPin, SlidersHorizontal } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, num, optStr, str } from '@/app/lib/a2ui/props.ts';
import {
  ArtifactCard,
  ArtifactHeader,
  PrimaryButton,
  ScorePill,
  SecondaryButton,
  VerificationBadge,
  readTier,
  type TierKey,
} from './procurement-parts.tsx';

const messages = defineMessages({
  kind: {
    id: 'shortlist.kind',
    defaultMessage: 'Supplier shortlist',
    description: 'Eyebrow label for the ranked supplier shortlist artifact.',
  },
  compare: {
    id: 'shortlist.compare',
    defaultMessage: 'Compare selected',
    description: 'Button that opens the side-by-side comparison of ticked suppliers.',
  },
  refine: {
    id: 'shortlist.refine',
    defaultMessage: 'Change my requirements',
    description: 'Button that loops back to refine the brief criteria.',
  },
  human: {
    id: 'shortlist.human',
    defaultMessage: 'Escalate to human sourcing',
    description: 'Button that escalates the request to a Shivurt account manager.',
  },
  meets: {
    id: 'shortlist.meets',
    defaultMessage: 'Meets',
    description: 'Heading above the criteria a supplier satisfies.',
  },
  tradeoffs: {
    id: 'shortlist.tradeoffs',
    defaultMessage: 'Trade-offs',
    description: 'Heading above the criteria a supplier does not satisfy.',
  },
  selectSupplier: {
    id: 'shortlist.selectSupplier',
    defaultMessage: 'Select {name} for comparison',
    description: 'Accessible label for the supplier selection checkbox.',
  },
  rank: {
    id: 'shortlist.rank',
    defaultMessage: 'Rank {position}',
    description: 'Small label showing a supplier position in the ranked shortlist.',
  },
  excludedHeading: {
    id: 'shortlist.excludedHeading',
    defaultMessage:
      '{count, plural, one {# supplier considered and excluded — show why} other {# suppliers considered and excluded — show why}}',
    description: 'Summary line of the collapsible list of suppliers that failed the filters.',
  },
  confidenceHigh: {
    id: 'shortlist.confidence.high',
    defaultMessage: 'High confidence',
    description: 'Label for a shortlist where verified suppliers match every requirement.',
  },
  confidenceMedium: {
    id: 'shortlist.confidence.medium',
    defaultMessage: 'Medium confidence',
    description: 'Label for a shortlist that involves compromises.',
  },
  confidenceLow: {
    id: 'shortlist.confidence.low',
    defaultMessage: 'Low confidence',
    description: 'Label for a shortlist that requires real trade-offs.',
  },
  relaxHeading: {
    id: 'shortlist.relaxHeading',
    defaultMessage: 'Widen the search',
    description: 'Heading above the filter-relaxing options.',
  },
  showUnverified: {
    id: 'shortlist.showUnverified',
    defaultMessage: 'Show unverified suppliers too',
    description: 'Button that asks for the secondary list of unverified suppliers.',
  },
  listedFor: {
    id: 'shortlist.listedFor',
    defaultMessage: 'Listed for {products}',
    description: 'Line showing the products the directory lists for this supplier.',
  },
  whyRanked: {
    id: 'shortlist.whyRanked',
    defaultMessage: 'Why it ranks here',
    description: 'Heading above the chips naming the rules a supplier score is made of.',
  },
  ruleProductMatch: {
    id: 'shortlist.rule.productMatch',
    defaultMessage: 'Makes what you asked for',
    description: 'Chip: the supplier passed the product-match filter.',
  },
  ruleMoqEligible: {
    id: 'shortlist.rule.moqEligible',
    defaultMessage: 'Takes your order size',
    description: 'Chip: the requested quantity meets the supplier minimum order.',
  },
  ruleVerifiedBoost: {
    id: 'shortlist.rule.verifiedBoost',
    defaultMessage: 'Shivurt verified',
    description: 'Chip: the supplier holds a confirmed verification tier.',
  },
  ruleLocationBoost: {
    id: 'shortlist.rule.locationBoost',
    defaultMessage: 'In your delivery location',
    description: 'Chip: the supplier is based in the buyer delivery location.',
  },
  ruleDocumentationPending: {
    id: 'shortlist.rule.documentationPending',
    defaultMessage: 'Verification still pending',
    description: 'Chip: verification documents submitted but not yet confirmed.',
  },
});

/** Chip label per rule, so a score reads as reasons rather than a bare number. */
const RULE_LABELS = {
  product_match: messages.ruleProductMatch,
  moq_eligible: messages.ruleMoqEligible,
  verified_boost: messages.ruleVerifiedBoost,
  location_boost: messages.ruleLocationBoost,
  documentation_pending: messages.ruleDocumentationPending,
} as const;

interface ShortlistSupplier {
  id: string;
  name: string;
  tier: TierKey;
  tierLabel: string;
  score?: number;
  earned: EarnedRule[];
  verifiedOn?: string;
  location?: string;
  listedFor?: string;
  highlights: string[];
  tradeoffs: string[];
  facts: string[];
}

/** The rule ids a score can be made of — anything else the model sends is dropped. */
const EARNED_RULES = [
  'product_match',
  'moq_eligible',
  'verified_boost',
  'location_boost',
  'documentation_pending',
] as const;

type EarnedRule = (typeof EARNED_RULES)[number];

function readEarned(value: unknown): EarnedRule[] {
  return arr(value)
    .map(str)
    .filter((entry): entry is EarnedRule => (EARNED_RULES as readonly string[]).includes(entry));
}

function readSuppliers(value: unknown): ShortlistSupplier[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry, index) => {
      const verifiedOn = optStr(entry.verifiedOn);
      const location = optStr(entry.location);
      const listedFor = optStr(entry.listedFor);
      const score = num(entry.score);
      return {
        id: str(entry.id) || `supplier-${index}`,
        name: str(entry.name),
        tier: readTier(entry.tier),
        tierLabel: str(entry.tierLabel),
        ...(score !== undefined ? { score } : {}),
        earned: readEarned(entry.earned),
        ...(verifiedOn ? { verifiedOn } : {}),
        ...(location ? { location } : {}),
        ...(listedFor ? { listedFor } : {}),
        highlights: arr(entry.highlights).map(str).filter(Boolean),
        tradeoffs: arr(entry.tradeoffs).map(str).filter(Boolean),
        facts: arr(entry.facts).map(str).filter(Boolean),
      };
    })
    .filter((entry) => entry.name.length > 0);
}

interface RelaxOption {
  label: string;
  detail?: string;
}

function readRelaxOptions(value: unknown): RelaxOption[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => {
      const detail = optStr(entry.detail);
      return { label: str(entry.label), ...(detail ? { detail } : {}) };
    })
    .filter((entry) => entry.label.length > 0);
}

const SupplierRow: FC<{
  supplier: ShortlistSupplier;
  position: number;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
}> = ({ supplier, position, selectable, selected, onToggle }) => {
  const intl = useIntl();
  const body = (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground-subtle">
          {intl.formatMessage(messages.rank, { position })}
        </span>
        <h3 className="min-w-0 text-body-lg font-semibold tracking-tight text-foreground" translate="no">
          {supplier.name}
        </h3>
        <VerificationBadge
          tier={supplier.tier}
          label={supplier.tierLabel}
          {...(supplier.verifiedOn ? { verifiedOn: supplier.verifiedOn } : {})}
        />
        {supplier.score !== undefined ? <ScorePill score={supplier.score} /> : null}
      </div>

      {supplier.location ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-body-sm text-muted-foreground">
          <MapPin aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0">{supplier.location}</span>
        </p>
      ) : null}

      {supplier.listedFor ? (
        <p className="mt-1 text-body-sm text-muted-foreground">
          {intl.formatMessage(messages.listedFor, { products: supplier.listedFor })}
        </p>
      ) : null}

      {supplier.facts.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {supplier.facts.map((fact) => (
            <li key={fact} className="rounded-sm bg-muted px-2 py-1 text-body-xs text-muted-foreground">
              {fact}
            </li>
          ))}
        </ul>
      ) : null}

      {supplier.earned.length > 0 ? (
        <div className="mt-3">
          <h4 className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground-subtle">
            {intl.formatMessage(messages.whyRanked)}
          </h4>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {supplier.earned.map((rule) => (
              <li
                key={rule}
                className={
                  rule === 'documentation_pending'
                    ? 'rounded-sm border border-warning/40 bg-warning/10 px-2 py-0.5 text-body-xs font-medium text-warning-foreground'
                    : 'rounded-sm border border-brand-accent/30 bg-brand-accent/10 px-2 py-0.5 text-body-xs font-medium text-foreground'
                }
              >
                {intl.formatMessage(RULE_LABELS[rule])}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {supplier.highlights.length > 0 ? (
          <div className="min-w-0">
            <h4 className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
              {intl.formatMessage(messages.meets)}
            </h4>
            <ul className="mt-1.5 flex flex-col gap-1">
              {supplier.highlights.map((highlight) => (
                <li key={highlight} className="flex items-start gap-1.5 text-body-sm text-foreground">
                  <Check aria-hidden="true" className="mt-1 h-3.5 w-3.5 shrink-0 text-success" />
                  <span className="min-w-0">{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {supplier.tradeoffs.length > 0 ? (
          <div className="min-w-0">
            <h4 className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
              {intl.formatMessage(messages.tradeoffs)}
            </h4>
            <ul className="mt-1.5 flex flex-col gap-1">
              {supplier.tradeoffs.map((tradeoff) => (
                <li
                  key={tradeoff}
                  className="flex items-start gap-1.5 text-body-sm text-muted-foreground"
                >
                  <AlertTriangle aria-hidden="true" className="mt-1 h-3.5 w-3.5 shrink-0 text-warning" />
                  <span className="min-w-0">{tradeoff}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );

  if (!selectable) {
    return <div className="flex gap-4 rounded-lg border border-border bg-card p-5">{body}</div>;
  }

  return (
    <label
      className="flex cursor-pointer touch-manipulation gap-4 rounded-lg border bg-card p-5 transition-colors duration-150 ease-out focus-within:outline focus-within:outline-2 focus-within:outline-offset-[3px] focus-within:outline-primary data-[selected=true]:border-brand-accent data-[selected=false]:border-border hover:bg-muted/40"
      data-selected={selected}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        aria-label={intl.formatMessage(messages.selectSupplier, { name: supplier.name })}
        className="mt-1 h-4 w-4 shrink-0 accent-primary"
      />
      {body}
    </label>
  );
};

export const SupplierShortlist: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const suppliers = readSuppliers(node.props.suppliers);
  const secondary = readSuppliers(node.props.secondarySuppliers);
  const relaxOptions = readRelaxOptions(node.props.relaxOptions);
  const emptyNote = optStr(node.props.emptyNote);
  const poolNote = optStr(node.props.poolNote);
  const excluded = arr(node.props.excluded)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => ({
      name: str(entry.name),
      tierLabel: optStr(entry.tierLabel),
      reason: str(entry.reason),
    }))
    .filter((entry) => entry.name.length > 0 && entry.reason.length > 0);
  const secondaryTitle = optStr(node.props.secondaryTitle);
  const confidence = str(node.props.confidence);
  const [selected, setSelected] = useState<string[]>(() => suppliers.map((entry) => entry.id));

  const toggle = (id: string) => {
    setSelected((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );
  };

  const confidenceLabel =
    confidence === 'high'
      ? intl.formatMessage(messages.confidenceHigh)
      : confidence === 'low'
        ? intl.formatMessage(messages.confidenceLow)
        : intl.formatMessage(messages.confidenceMedium);
  const confidenceClass =
    confidence === 'high'
      ? 'bg-success/12 text-success'
      : confidence === 'low'
        ? 'bg-destructive/10 text-destructive'
        : 'bg-warning/14 text-warning';

  return (
    <ArtifactCard>
      <ArtifactHeader
        kind={intl.formatMessage(messages.kind)}
        title={str(node.props.title)}
        sourceNote={str(node.props.sourceNote)}
        status={
          <span
            className={`rounded-sm px-2 py-1 text-body-xs font-semibold ${confidenceClass}`}
          >
            {confidenceLabel}
          </span>
        }
      />

      <p className="text-body-sm text-muted-foreground">{str(node.props.confidenceNote)}</p>

      {suppliers.length === 0 ? (
        <div className="rounded-lg bg-muted p-5">
          <p className="flex items-start gap-2 text-body-sm text-foreground">
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span className="min-w-0">{emptyNote}</span>
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-4">
          {suppliers.map((supplier, index) => (
            <li key={supplier.id}>
              <SupplierRow
                supplier={supplier}
                position={index + 1}
                selectable
                selected={selected.includes(supplier.id)}
                onToggle={() => toggle(supplier.id)}
              />
            </li>
          ))}
        </ul>
      )}

      {poolNote ? (
        <p className="flex items-start gap-2 rounded-lg bg-warning/10 p-4 text-body-sm text-foreground">
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span className="min-w-0">{poolNote}</span>
        </p>
      ) : null}

      {excluded.length > 0 ? (
        <details className="rounded-lg border border-border bg-card">
          <summary className="cursor-pointer list-none px-4 py-3 text-body-sm font-semibold text-foreground">
            {intl.formatMessage(messages.excludedHeading, { count: excluded.length })}
          </summary>
          <ul className="flex flex-col divide-y divide-border border-t border-border">
            {excluded.map((entry) => (
              <li key={`${entry.name}-${entry.reason}`} className="px-4 py-3">
                <p className="text-body-sm font-semibold text-foreground" translate="no">
                  {entry.name}
                  {entry.tierLabel ? (
                    <span className="ml-2 font-normal text-muted-foreground-subtle">
                      {entry.tierLabel}
                    </span>
                  ) : null}
                </p>
                <p className="mt-0.5 text-body-sm text-muted-foreground">{entry.reason}</p>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {relaxOptions.length > 0 ? (
        <section className="rounded-lg bg-muted p-4">
          <h2 className="flex items-center gap-2 text-body-sm font-semibold text-foreground">
            <SlidersHorizontal aria-hidden="true" className="h-4 w-4 text-brand-accent" />
            {intl.formatMessage(messages.relaxHeading)}
          </h2>
          <ul className="mt-3 flex flex-col gap-2">
            {relaxOptions.map((option) => (
              <li key={option.label}>
                <button
                  type="button"
                  onClick={() => dispatch?.('relaxFilter', { option: option.label })}
                  className="w-full touch-manipulation rounded-input border border-border bg-card px-4 py-2.5 text-left transition-colors duration-150 ease-out hover:border-brand-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-[0.99]"
                >
                  <span className="block text-body-sm font-semibold text-foreground">
                    {option.label}
                  </span>
                  {option.detail ? (
                    <span className="mt-0.5 block text-body-xs text-muted-foreground">
                      {option.detail}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {secondary.length > 0 && secondaryTitle ? (
        <section>
          <h2 className="text-body-sm font-semibold tracking-tight text-foreground">
            {secondaryTitle}
          </h2>
          <ul className="mt-3 flex flex-col gap-4">
            {secondary.map((supplier, index) => (
              <li key={supplier.id}>
                <SupplierRow
                  supplier={supplier}
                  position={index + 1}
                  selectable={false}
                  selected={false}
                  onToggle={() => undefined}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {suppliers.length > 0 ? (
          <PrimaryButton
            label={intl.formatMessage(messages.compare)}
            disabled={selected.length === 0}
            onClick={() => dispatch?.('compareSelected', { selectedIds: selected.join(',') })}
          />
        ) : null}
        <SecondaryButton
          label={intl.formatMessage(messages.refine)}
          onClick={() => dispatch?.('refineCriteria', {})}
        />
        {secondary.length === 0 ? (
          <SecondaryButton
            label={intl.formatMessage(messages.showUnverified)}
            onClick={() => dispatch?.('showUnverified', {})}
          />
        ) : null}
        <SecondaryButton
          label={intl.formatMessage(messages.human)}
          onClick={() => dispatch?.('requestHumanSourcing', {})}
        />
      </div>
    </ArtifactCard>
  );
};
